import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notify, notifyError } from '../notify';
import GuideView from '../components/GuideView';

// Reply Engine: one knowledge base per text subreddit, built by the scraper
// from the sub's top questions and answers. Comment Mode uses it on its own;
// a subreddit without one is queued the first time a VA writes a comment there.

const STATUS = {
  pending: { label: 'Queued', color: '#888888' },
  building: { label: 'Building', color: '#f59e0b' },
  ready: { label: 'Ready', color: '#22c55e' },
  failed: { label: 'Failed', color: '#ef4444' },
  rejected: { label: 'Not usable', color: '#ef4444' }
};
const MODEL_LABEL = { sonnet: 'Sonnet 5', grok: 'Grok' };
const isBusy = (kb) => kb.status === 'pending' || kb.status === 'building';

function age(iso) {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days < 1 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
}

function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status, color: '#888888' };
  return <span className="status-badge" style={{ background: s.color }}>{s.label}</span>;
}

function StatBox({ value, label }) {
  return (
    <div className="stat-box">
      <div className="stat-box-value">{value}</div>
      <div className="stat-box-label">{label}</div>
    </div>
  );
}

function KbRow({ kb, onOpen, open }) {
  return (
    <div className="list-item" style={{ borderColor: open ? '#ff4500' : undefined }}>
      <div className="list-item-main">
        <strong>r/{kb.subreddit}<StatusBadge status={kb.status} /></strong>
        <span className="sub">
          {kb.built_at ? `Built ${age(kb.built_at)} from ${kb.question_count} questions and ${kb.answer_count} answers` : 'Not built yet'}
          {kb.reply_model ? ` · replies by ${MODEL_LABEL[kb.reply_model]}` : ''}
          {kb.is_nsfw ? ' · NSFW' : ''}
        </span>
        {isBusy(kb) && kb.progress && <span className="sub" style={{ color: '#f59e0b' }}>{kb.progress}</span>}
        {(kb.status === 'failed' || kb.status === 'rejected') && kb.last_error && <span className="sub" style={{ color: '#ef4444' }}>{kb.last_error}</span>}
      </div>
      <div className="list-item-actions">
        <button className="btn-secondary btn-small" onClick={() => onOpen(open ? null : kb.id)}>{open ? 'Close' : 'Open'}</button>
      </div>
    </div>
  );
}

function BuildForm({ onBuilt }) {
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const build = async () => {
    if (!name.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/reply-kbs`, { subreddit: name.trim() });
      notify(`r/${res.data.subreddit} queued: the scraper builds it in about 10 minutes`, 'success');
      setName('');
      onBuilt();
    } catch (err) {
      notifyError(err, 'Could not queue the build');
    }
    setSending(false);
  };
  return (
    <div className="card">
      <h2>Build a knowledge base</h2>
      <div className="form-row">
        <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && build()} placeholder="Subreddit, e.g. AskReddit" />
        <button onClick={build} disabled={sending}>{sending ? 'Queuing…' : 'Build'}</button>
      </div>
      <p className="sub" style={{ marginTop: '8px' }}>Text subreddits only. It studies the top 30 questions of the year and top 60 of the month with their top answers: about 10 minutes and $0.60, once.</p>
    </div>
  );
}

function StatsRow({ stats }) {
  const a = (stats && stats.answers) || null;
  if (!a) return null;
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '12px 0' }}>
      <StatBox value={`${a.top_words.median} words`} label="Typical #1 answer" />
      <StatBox value={`${a.top_short_pct}%`} label="#1 answers of 15 words or fewer" />
      <StatBox value={`${a.top_within_15_min_pct}%`} label="#1 posted within 15 min" />
      <StatBox value={`${a.first_person_pct}%`} label="Answers in first person" />
    </div>
  );
}

function TryIt({ kb, creators }) {
  const [creatorId, setCreatorId] = useState('');
  const [question, setQuestion] = useState('');
  const [postText, setPostText] = useState('');
  const [result, setResult] = useState(null);
  const [writing, setWriting] = useState(false);

  const write = async () => {
    if (!question.trim()) return;
    setWriting(true);
    try {
      const res = await axios.post(`${API}/api/ai/generate-comments`, {
        post_title: question.trim(), post_body: postText.trim() || undefined, subreddit: kb.subreddit,
        creator_id: creatorId || undefined
      });
      setResult(res.data);
    } catch (err) {
      notifyError(err, 'Could not write replies');
    }
    setWriting(false);
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <h3 style={{ fontSize: '15px', marginBottom: '8px' }}>Try it</h3>
      <div className="form-grid">
        <input value={question} onChange={e => setQuestion(e.target.value)} placeholder={`A question from r/${kb.subreddit}`} />
        <textarea rows={2} value={postText} onChange={e => setPostText(e.target.value)} placeholder="Post text under the title (optional)" />
        <div className="form-row">
          <select value={creatorId} onChange={e => setCreatorId(e.target.value)}>
            <option value="">No profile card</option>
            {(creators || []).filter(c => c.name !== 'Unassigned').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={write} disabled={writing || !question.trim()}>{writing ? 'Writing…' : 'Write 3 replies'}</button>
        </div>
      </div>
      {result && <TryResult result={result} />}
    </div>
  );
}

function TryResult({ result }) {
  const kb = result.kb || {};
  const note = kb.used
    ? `Written by ${MODEL_LABEL[kb.model] || kb.model} from the r/${kb.subreddit} guide${kb.profile ? ', with the profile card' : ', no profile card'}`
    : `Not from the guide (${kb.error || kb.status || 'no knowledge base'}): today's prompt wrote these`;
  return (
    <div style={{ marginTop: '12px' }}>
      <p className="sub" style={{ color: kb.used ? '#22c55e' : '#f59e0b' }}>{note}</p>
      {(result.variations || []).map((text, i) => (
        <div key={i} className="list-item" style={{ marginTop: '8px', display: 'block' }}>
          {result.styles && result.styles[i] && <span className="tag" style={{ marginRight: '8px' }}>{result.styles[i]}</span>}
          {text}
        </div>
      ))}
    </div>
  );
}

function QuestionIdeas({ kb }) {
  const [ideas, setIdeas] = useState(null);
  const [writing, setWriting] = useState(false);
  const write = async () => {
    setWriting(true);
    try {
      const res = await axios.post(`${API}/api/reply-kbs/${kb.id}/question-ideas`, { count: 10 });
      setIdeas(res.data.ideas);
    } catch (err) {
      notifyError(err, 'Could not write question ideas');
    }
    setWriting(false);
  };
  return (
    <div style={{ marginTop: '20px' }}>
      <div className="form-row">
        <h3 style={{ fontSize: '15px', margin: 0 }}>New question ideas</h3>
        <button className="btn-secondary btn-small" onClick={write} disabled={writing}>{writing ? 'Writing…' : 'Write 10'}</button>
      </div>
      {ideas && ideas.map((q, i) => (
        <div key={i} className="list-item" style={{ marginTop: '8px', display: 'block' }}>
          <span className="tag" style={{ marginRight: '8px' }}>{q.type}</span>{q.title}
          {q.repeats && <div className="sub" style={{ color: '#ef4444', marginTop: '4px' }}>Too close to an earlier question: "{q.repeats}"</div>}
        </div>
      ))}
    </div>
  );
}

const GUIDES = [
  { key: 'reply_guide', label: 'Reply guide', about: 'The prompt behind every reply in this subreddit.', saved: 'every reply in this subreddit now uses it' },
  { key: 'question_guide', label: 'Question guide', about: 'The prompt behind the question ideas.', saved: 'question ideas now use it' }
];

// Grows with its text, so a long guide scrolls with the page, not inside a box.
function GuideEditor({ value, onChange }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    ref.current.style.height = 'auto';
    ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%', fontSize: '14px', lineHeight: 1.6, overflow: 'hidden', resize: 'none' }} />;
}

function Guides({ kb, onSaved }) {
  const [shown, setShown] = useState(GUIDES[0]);
  const [draft, setDraft] = useState(null); // the text being edited; null while reading
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/reply-kbs/${kb.id}`, { [shown.key]: draft });
      notify(`${shown.label} saved: ${shown.saved}`, 'success');
      setDraft(null);
      onSaved();
    } catch (err) {
      notifyError(err, 'Could not save the guide');
    }
    setSaving(false);
  };
  if (!kb.reply_guide) return null;
  const editing = draft !== null;
  return (
    <div style={{ marginTop: '24px' }}>
      <div className="form-row" style={{ flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '15px', margin: 0 }}>Guides</h3>
        {GUIDES.map(g => (
          <button key={g.key} className={g === shown ? 'btn-small' : 'btn-secondary btn-small'} onClick={() => setShown(g)} disabled={editing}>{g.label}</button>
        ))}
        {kb.guides_edited_at && <span className="sub">edited by hand {age(kb.guides_edited_at)}</span>}
        <span style={{ flex: 1 }} />
        {editing ? (
          <>
            <button className="btn-secondary btn-small" onClick={() => setDraft(null)} disabled={saving}>Cancel</button>
            <button className="btn-small" onClick={save} disabled={saving || !draft.trim() || draft === kb[shown.key]}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        ) : <button className="btn-secondary btn-small" onClick={() => setDraft(kb[shown.key] || '')}>Edit</button>}
      </div>
      <p className="sub" style={{ margin: '6px 0 14px' }}>{shown.about}</p>
      {editing ? <GuideEditor value={draft} onChange={setDraft} /> : <GuideView text={kb[shown.key]} />}
    </div>
  );
}

function KbDetail({ id, role, creators, onChanged, onClose }) {
  const [kb, setKb] = useState(null);
  const load = useCallback(() => axios.get(`${API}/api/reply-kbs/${id}`)
    .then(res => setKb(res.data))
    .catch(err => notifyError(err, 'Could not load the knowledge base')), [id]);
  useEffect(() => { load(); }, [load]);
  const busy = kb ? isBusy(kb) : false;
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [busy, load]);

  const act = async (request, done) => {
    try {
      await request();
      if (done) notify(done, 'success');
      load();
      onChanged();
    } catch (err) {
      notifyError(err, 'That did not work');
    }
  };
  const setModel = (reply_model) => act(() => axios.put(`${API}/api/reply-kbs/${id}`, { reply_model }), `Replies in r/${kb.subreddit} now come from ${MODEL_LABEL[reply_model]}`);
  const rebuild = () => {
    const edits = kb.guides_edited_at ? ' Your hand edits to the guides will be replaced.' : '';
    if (!window.confirm(`Rebuild r/${kb.subreddit}? The scraper collects it again and writes new guides (about 10 minutes, $0.60). The current guides stay in use until then.${edits}`)) return;
    act(() => axios.post(`${API}/api/reply-kbs/${id}/rebuild`), 'Rebuild queued');
  };
  const remove = () => {
    if (!window.confirm(`Delete the r/${kb.subreddit} knowledge base? Comment Mode will queue a new build the next time a VA writes there.`)) return;
    act(() => axios.delete(`${API}/api/reply-kbs/${id}`), 'Deleted').then(onClose);
  };

  if (!kb) return <div className="card"><p className="empty">Loading…</p></div>;
  return (
    <div className="card">
      <div className="list-header">
        <div>
          <h2 style={{ margin: 0 }}>r/{kb.subreddit}<StatusBadge status={kb.status} /></h2>
          <span className="sub">
            {kb.built_at ? `Built ${age(kb.built_at)}` : 'Not built yet'}
            {kb.cost_usd ? ` · study cost $${Number(kb.cost_usd).toFixed(2)}` : ''}
            {kb.requested_by_name ? ` · requested by ${kb.requested_by_name}` : ''}
          </span>
        </div>
        <div className="list-item-actions">
          <select value={kb.reply_model || 'sonnet'} onChange={e => setModel(e.target.value)} title="The model that writes the replies">
            <option value="sonnet">Replies: Sonnet 5</option>
            <option value="grok">Replies: Grok</option>
          </select>
          <button className="btn-secondary btn-small" onClick={rebuild} disabled={busy}>Rebuild</button>
          {role === 'super_admin' && <button className="btn-danger btn-small" onClick={remove}>Delete</button>}
        </div>
      </div>
      {busy && <p className="sub" style={{ color: '#f59e0b' }}>{kb.progress || 'Waiting for a scraper lane'}{kb.reply_guide ? ' · the current guides stay in use until the rebuild finishes' : ''}</p>}
      {kb.last_error && !busy && <p className="sub" style={{ color: '#ef4444' }}>{kb.last_error}</p>}
      <StatsRow stats={kb.stats} />
      {kb.reply_guide && <TryIt kb={kb} creators={creators} />}
      {kb.question_guide && <QuestionIdeas kb={kb} />}
      <Guides key={`${kb.id}-${kb.built_at}`} kb={kb} onSaved={() => { load(); onChanged(); }} />
    </div>
  );
}

function ReplyEngine({ role, creators }) {
  const [kbs, setKbs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => axios.get(`${API}/api/reply-kbs`)
    .then(res => setKbs(res.data))
    .catch(err => notifyError(err, 'Could not load the Reply Engine'))
    .finally(() => setLoaded(true)), []);
  useEffect(() => { load(); }, [load]);
  const anyBusy = kbs.some(isBusy);
  useEffect(() => {
    if (!anyBusy) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [anyBusy, load]);

  return (
    <div className="page">
      <h1>Reply Engine</h1>
      <p>One knowledge base per text subreddit, built from its top questions and answers. Comment Mode uses it on its own; a subreddit without one is queued the first time a VA writes there.</p>
      <BuildForm onBuilt={load} />
      <div className="card">
        <h2>Subreddits</h2>
        {!loaded ? <p className="empty">Loading…</p>
          : kbs.length === 0 ? <p className="empty">No knowledge bases yet. Build one above.</p>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {kbs.map(kb => <KbRow key={kb.id} kb={kb} open={openId === kb.id} onOpen={setOpenId} />)}
            </div>}
      </div>
      {openId && <KbDetail key={openId} id={openId} role={role} creators={creators} onChanged={load} onClose={() => setOpenId(null)} />}
    </div>
  );
}

export default ReplyEngine;
